import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import '../assets/css/contact.css';
import '../assets/css/psearch.css';
import SignInModal from './SignInModal';
import Nav from './Nav';
import SEO from './SEO';

function BrandPartnership() {
  const [user, setUser] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInModalMessage, setSignInModalMessage] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    getSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        console.error('Error signing in:', error.message);
        setAuthMessage(`Sign in error: ${error.message}`);
      } else {
        console.log('OAuth initiated:', data);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setAuthMessage(`Unexpected error: ${err.message}`);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!authEmail) return;
    
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithOtp({
      email: authEmail,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email for the magic link!');
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignIn = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setShowSignInModal(false);
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignUp = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: { user_role: 'general' },
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email to confirm your account!');
    }
    setAuthLoading(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setShowUserDropdown(false);
  };

  const [formData, setFormData] = useState({
    brandName: '',
    contactName: '',
    email: '',
    website: '',
    instagram: '',
    productCategories: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.apispreadsheets.com/data/f3wYvYFoCfw2XxoV/';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submissionData = {
        brandName: formData.brandName,
        contactName: formData.contactName,
        email: formData.email,
        website: formData.website,
        instagram: formData.instagram,
        productCategories: formData.productCategories,
        message: formData.message,
        timestamp: new Date().toISOString()
      };

      const response = await fetch(process.env.REACT_APP_BRAND_PARTNERSHIP_SCRIPT_URL, {
        method: "POST",
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      setModalMessage('Thank you! Your partnership request has been submitted. We\'ll be in touch soon.');
      setShowModal(true);
      setFormData({
        brandName: '',
        contactName: '',
        email: '',
        website: '',
        instagram: '',
        productCategories: '',
        message: ''
      });
      setTimeout(() => {
        setShowModal(false);
      }, 4000);
    } catch (error) {
      setModalMessage('An error occurred. Please try again.');
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <SEO
        title="Partner With Us — Kashmiri Brand Partnerships"
        description="Partner with Yemberzal to showcase your Kashmiri fashion brand to a global audience. Join 50+ brands on Kashmir's leading fashion discovery platform."
        path="/partner"
      />
      <div className="container contact-shell">
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <p>{modalMessage}</p>
              <button onClick={() => setShowModal(false)}>OK</button>
            </div>
          </div>
        )}
        <Nav />

        <section id="partnership" className="contact-hero">
          <div className="contact-hero-inner">
            <div className="page-hero-fullwidth" style={{ 
              backgroundColor: '#f5f5f5', 
              paddingTop: '40px',
              paddingBottom: '20px',
              marginBottom: '40px',
              textAlign: 'left',
            }}>
              <h1 className="contact-title" style={{ fontSize: '3rem', fontWeight: 600, fontFamily: "'Montserrat', sans-serif", color: '#1a1a1a', marginBottom: '8px', textAlign: 'left' }}>
                PARTNER WITH US
              </h1>
              <p className="contact-subtitle" style={{ fontSize: '16px', color: '#666', fontWeight: '300', textAlign: 'left' }}>
                Join Yemberzal and showcase your Kashmiri fashion brand to a global audience. <br />
                We're looking for authentic brands that celebrate the rich heritage of Kashmiri craftsmanship.
              </p>
            </div>
          </div>
        </section>

        <section className="contact-form-section">
          <div className="contact-form-inner">
            <div className="contact-card">
              <div className="contact-formWrap">
                <h3 className="form-heading">Brand Partnership Form</h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="brandName" className="form-label">Brand Name *</label>
                  <input
                    type="text"
                    name="brandName"
                    value={formData.brandName}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Your brand name"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="contactName" className="form-label">Contact Person *</label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="website" className="form-label">Website(If available)</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="https://yourbrand.com"
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="instagram" className="form-label">Instagram Handle(Optional)</label>
                  <input
                    type="text"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="@yourbrand"
                  />
                </div>

                {/* <div className="mb-3">
                  <label htmlFor="productCategories" className="form-label">Product Categories *</label>
                  <input
                    type="text"
                    name="productCategories"
                    value={formData.productCategories}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="e.g., Pashmina, Pheran, Jewelry, Carpets"
                    required
                  />
                </div> */}

                <div className="mb-3">
                  <label htmlFor="message" className="form-label">Tell us about your brand</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-control"
                    rows="4"
                    placeholder="Share your brand story, what makes your products unique, and why you'd like to partner with Yemberzal..."
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: '#1a1a1a',
                    borderColor: '#1a1a1a',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '500',
                    borderRadius: '0px'
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Partnership Request'}
                </button>

                <div style={{ 
                  marginTop: '24px', 
                  paddingTop: '24px', 
                  borderTop: '1px solid #e8e4df',
                  textAlign: 'center'
                }}>
                  <p style={{ color: '#6b6560', fontSize: '14px', marginBottom: '12px' }}>
                    Already a brand partner?
                  </p>
                  <Link 
                    to="/brand-portal/login" 
                    style={{
                      display: 'inline-block',
                      padding: '10px 24px',
                      border: '1px solid #2d2a26',
                      borderRadius: '0px',
                      color: '#2d2a26',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Access Brand Portal →
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
        </section>
      </div>

      <SignInModal
        open={showSignInModal}
        onClose={() => {
          setShowSignInModal(false);
          setAuthMessage('');
          setAuthEmail('');
          setSignInModalMessage('');
        }}
        message={signInModalMessage}
        authEmail={authEmail}
        setAuthEmail={setAuthEmail}
        authLoading={authLoading}
        authMessage={authMessage}
        onEmailSignIn={handleEmailSignIn}
        onGoogleSignIn={handleGoogleSignIn}
        onEmailPasswordSignIn={handleEmailPasswordSignIn}
        onEmailPasswordSignUp={handleEmailPasswordSignUp}
      />
    </div>
  );
}

export default BrandPartnership;
